/**
 * B2C access helpers (pure, unit-testable via source contracts).
 * Clinic surfaces were removed in Phase 6; bookmarked URLs redirect to consumer home.
 * Platform dashboard remains available for platform superadmins only.
 */

/** Authenticated consumer home (new case). Public marketing lives at `/`. */
export const CONSUMER_HOME_PATH = "/case/new";

/** Consumer health-profile onboarding (Tier A) — not clinic org onboarding. */
export const HEALTH_PROFILE_ONBOARDING_PATH = "/onboarding/profile";

/** Legacy clinic-primary paths — always redirect in B2C builds. */
export const CLINIC_PRIMARY_PATHS = [
  "/onboarding",
  "/admin",
  "/patients",
  "/voice",
];

/** Platform operator dashboard — superadmin only (not in CLINIC_PRIMARY_PATHS). */
export const PLATFORM_PATH = "/platform";

/** Legacy investor demo route — removed in Phase 6. */
export const DEMO_MOLECULAR_PATH = "/demo/molecular";

/**
 * @param {object | null | undefined} user — `/api/auth/me` payload
 * @returns {boolean}
 */
export function isPlatformSuperadmin(user) {
  return user?.role === "superadmin";
}

/**
 * @param {object | null | undefined} user
 * @returns {boolean}
 */
export function isHealthProfileTierAComplete(user) {
  return Boolean(user?.health_profile_summary?.tier_a_complete);
}

/**
 * B2C: force Tier A health profile after signup / first login.
 * Clinic org onboarding remains disabled (always false there).
 */
export function shouldForceHealthProfileOnboarding(user, pathname) {
  if (!user) return false;
  if (isHealthProfileTierAComplete(user)) return false;
  if (
    pathname === HEALTH_PROFILE_ONBOARDING_PATH ||
    pathname.startsWith(`${HEALTH_PROFILE_ONBOARDING_PATH}/`)
  ) {
    return false;
  }
  // Allow auth/privacy/checkout without blocking
  if (
    pathname.startsWith("/login") ||
    pathname.startsWith("/signup") ||
    pathname.startsWith("/auth/") ||
    pathname.startsWith("/privacy") ||
    pathname.startsWith("/terms") ||
    pathname.startsWith("/cookies") ||
    pathname.startsWith("/reset-password") ||
    pathname.startsWith("/checkout/")
  ) {
    return false;
  }
  return true;
}

/**
 * B2C: never force clinic onboarding when the user has no org_id.
 * Kept as an explicit predicate so tests and App.js share one rule.
 */
export function shouldForceOnboarding(_user, _pathname) {
  return false;
}

/**
 * @param {string} pathname
 * @param {object | null | undefined} [user] — when set, superadmins may use `/platform`
 * @returns {string | null} home redirect target when the path is clinic-only
 */
export function clinicRouteRedirect(pathname, user = null) {
  if (pathname === PLATFORM_PATH || pathname.startsWith(`${PLATFORM_PATH}/`)) {
    return isPlatformSuperadmin(user) ? null : CONSUMER_HOME_PATH;
  }
  // Consumer health onboarding must not be treated as legacy clinic /onboarding.
  if (
    pathname === HEALTH_PROFILE_ONBOARDING_PATH ||
    pathname.startsWith(`${HEALTH_PROFILE_ONBOARDING_PATH}/`)
  ) {
    return null;
  }
  for (const path of CLINIC_PRIMARY_PATHS) {
    if (pathname === path || pathname.startsWith(`${path}/`)) {
      return CONSUMER_HOME_PATH;
    }
  }
  if (pathname === DEMO_MOLECULAR_PATH || pathname.startsWith(`${DEMO_MOLECULAR_PATH}/`)) {
    return CONSUMER_HOME_PATH;
  }
  return null;
}

/**
 * @param {string} href
 * @param {object | null | undefined} [user]
 * @returns {boolean}
 */
export function isClinicNavEnabled(href, user = null) {
  if (href === PLATFORM_PATH) {
    return isPlatformSuperadmin(user);
  }
  return false;
}
